import type {
  AdminMembership,
  AdminMembershipDetail,
  AdminMembershipListResult,
  AdminGrantMembershipInput,
  CancelMembershipInput,
  MembershipEvent,
  MembershipGrantGroup,
  MembershipGrantGroupUpsertInput,
  MembershipPlan,
  MembershipPlanUpsertInput,
  StoredMembershipStatus,
} from "@yahshi/contracts";
import { adminMembershipListQuerySchema, effectiveMembershipStatus, isMembershipActive, membershipGrantGroupUpsertSchema, membershipPlanListQuerySchema, membershipPlanUpsertSchema } from "@yahshi/contracts";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { MembershipApi } from "./MembershipApi";

const now = () => new Date().toISOString();

function event(membershipId: string, type: MembershipEvent["type"], toStatus: StoredMembershipStatus | "none", extra: Partial<MembershipEvent> = {}): MembershipEvent {
  return {
    id: `mev_${membershipId}_${type}_${toStatus}`,
    membershipId,
    type,
    fromStatus: extra.fromStatus ?? null,
    toStatus,
    eventKey: extra.eventKey ?? null,
    channel: extra.channel ?? null,
    note: extra.note ?? "状态变更",
    createdAt: extra.createdAt ?? now(),
  };
}

function withEffective(item: AdminMembership, at = new Date()): AdminMembership {
  const status = effectiveMembershipStatus(item.status, item.expiresAt, at) as StoredMembershipStatus;
  return { ...item, status, member: isMembershipActive(item.status, item.expiresAt, at) };
}

export const seedMemberships: AdminMembershipDetail[] = [
  {
    membership: {
      id: "memb_demo_20260915001", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "11111111-1111-4111-8111-111111111111", userPublicId: "1001", userName: "林默",
      member: true, status: "active", level: "member", levelName: null, levelNameUg: null,
      expiresAt: "2026-10-15T02:18:00.000Z", startedAt: "2026-09-15T02:18:00.000Z", canceledAt: null,
      idempotencyKey: "idem_demo_member_001", version: 1,
      createdAt: "2026-09-15T02:18:00.000Z", updatedAt: "2026-09-15T02:18:00.000Z",
    },
    events: [
      event("memb_demo_20260915001", "status", "active", { fromStatus: "none", createdAt: "2026-09-15T02:18:00.000Z", note: "开通会员" }),
      event("memb_demo_20260915001", "notification", "active", { fromStatus: "active", eventKey: "membership.granted", channel: "in_app", note: "站内信已记录，未发送短信", createdAt: "2026-09-15T02:18:01.000Z" }),
    ],
  },
  {
    membership: {
      id: "memb_demo_20260908002", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "55555555-5555-4555-8555-555555555555", userPublicId: "1005", userName: "程一",
      member: false, status: "expired", level: "year", levelName: null, levelNameUg: null,
      expiresAt: "2026-09-01T05:20:00.000Z", startedAt: "2025-09-08T05:20:00.000Z", canceledAt: null,
      idempotencyKey: "idem_demo_member_002", version: 1,
      createdAt: "2025-09-08T05:20:00.000Z", updatedAt: "2026-09-01T05:20:00.000Z",
    },
    events: [event("memb_demo_20260908002", "status", "active", { fromStatus: "none", createdAt: "2025-09-08T05:20:00.000Z", note: "开通会员" })],
  },
  {
    membership: {
      id: "memb_demo_20260909003", appId: "app_yahshi_admin", appName: "Yahshi Admin", appCode: "yahshi-admin",
      userId: "33333333-3333-4333-8333-333333333333", userPublicId: "1003", userName: "周野",
      member: false, status: "canceled", level: "member", levelName: null, levelNameUg: null,
      expiresAt: "2026-12-01T08:20:00.000Z", startedAt: "2026-09-07T08:20:00.000Z", canceledAt: "2026-09-09T03:40:00.000Z",
      idempotencyKey: "idem_demo_member_003", version: 2,
      createdAt: "2026-09-07T08:20:00.000Z", updatedAt: "2026-09-09T03:40:00.000Z",
    },
    events: [
      event("memb_demo_20260909003", "status", "active", { fromStatus: "none", createdAt: "2026-09-07T08:20:00.000Z", note: "开通会员" }),
      event("memb_demo_20260909003", "status", "canceled", { fromStatus: "active", createdAt: "2026-09-09T03:40:00.000Z", note: "取消会员" }),
      event("memb_demo_20260909003", "notification", "canceled", { fromStatus: "canceled", eventKey: "membership.canceled", channel: "in_app", note: "站内信已记录，未发送短信", createdAt: "2026-09-09T03:40:01.000Z" }),
    ],
  },
  {
    membership: {
      id: "memb_demo_20260906004", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "44444444-4444-4444-8444-444444444444", userPublicId: "1004", userName: "顾言",
      member: true, status: "active", level: "year", levelName: null, levelNameUg: null,
      expiresAt: "2027-09-06T04:12:00.000Z", startedAt: "2026-09-06T04:12:00.000Z", canceledAt: null,
      idempotencyKey: "idem_demo_member_004", version: 1,
      createdAt: "2026-09-06T04:12:00.000Z", updatedAt: "2026-09-06T04:12:00.000Z",
    },
    events: [event("memb_demo_20260906004", "status", "active", { fromStatus: "none", createdAt: "2026-09-06T04:12:00.000Z", note: "开通会员" })],
  },
];

type MembershipAppLookup = { name: string; code: string; status?: string };

const demoApps: Record<string, MembershipAppLookup> = {
  app_yahshi_web: { name: "Yahshi Web", code: "yahshi-web", status: "active" },
  app_yahshi_admin: { name: "Yahshi Admin", code: "yahshi-admin", status: "active" },
};

const seedPlans: MembershipPlan[] = [
  { appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web", level: "member", name: "会员", nameUg: "ئەزا", amountMinor: 1990, currency: "CNY", periodDays: 30, period: "month", version: 1, updatedAt: "2026-09-15T02:18:00.000Z" },
  { appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web", level: "year", name: "年度会员", nameUg: "يىللىق ئەزا", amountMinor: 12800, currency: "CNY", periodDays: 365, period: "year", version: 1, updatedAt: "2026-09-06T04:12:00.000Z" },
  { appId: "app_yahshi_admin", appName: "Yahshi Admin", appCode: "yahshi-admin", level: "member", name: "会员", nameUg: "ئەزا", amountMinor: 1990, currency: "CNY", periodDays: 30, period: "month", version: 1, updatedAt: "2026-09-07T08:20:00.000Z" },
];

const seedGrantGroups: MembershipGrantGroup[] = [
  {
    ownerAppId: "app_yahshi_web", ownerAppName: "Yahshi Web", ownerAppCode: "yahshi-web",
    memberAppIds: ["app_yahshi_admin"],
    memberApps: [{ id: "app_yahshi_admin", name: "Yahshi Admin", code: "yahshi-admin" }],
    version: 1, updatedAt: "2026-09-16T02:00:00.000Z",
  },
];

const demoUsers: Record<string, { publicId: string; name: string }> = {
  "11111111-1111-4111-8111-111111111111": { publicId: "1001", name: "林默" },
  "33333333-3333-4333-8333-333333333333": { publicId: "1003", name: "周野" },
  "44444444-4444-4444-8444-444444444444": { publicId: "1004", name: "顾言" },
  "55555555-5555-4555-8555-555555555555": { publicId: "1005", name: "程一" },
};

function matchesQuery(item: AdminMembership, query: ReturnType<typeof adminMembershipListQuerySchema.parse>): boolean {
  const keyword = query.keyword.trim().toLowerCase();
  const haystack = `${item.id} ${item.level} ${item.levelName ?? ""} ${item.levelNameUg ?? ""} ${item.userName} ${item.userPublicId} ${item.appName} ${item.appCode}`.toLowerCase();
  if (keyword && !haystack.includes(keyword)) return false;
  if (query.appId && item.appId !== query.appId) return false;
  return true;
}

function namesFor(plans: MembershipPlan[], appId: string, level: string): Pick<AdminMembership, "levelName" | "levelNameUg"> {
  const plan = plans.find((item) => item.appId === appId && item.level === level);
  return { levelName: plan?.name ?? null, levelNameUg: plan?.nameUg ?? null };
}

export type CommerceOrderFulfillInput = {
  id: string;
  appId: string;
  userId: string;
  payload: Record<string, unknown>;
  paidAt: string | null;
};

export type DevelopmentMembershipApi = MembershipApi & {
  fulfillFromPaidOrder(order: CommerceOrderFulfillInput): Promise<void>;
};

export function createDevelopmentMembershipApi(options: {
  inbox?: { enqueue(input: { appId: string; appName: string; userId: string; eventKey: string; occurrenceKey: string; category: "transactional"; title: string; titleUg?: string; body: string; bodyUg?: string; createdAt?: string }): unknown };
  webhooks?: { enqueue(input: { appId: string; eventKey: "membership.granted" | "membership.canceled"; occurrenceKey: string; payload: Record<string, unknown> }): unknown };
  getApp?: (id: string) => Promise<MembershipAppLookup>;
} = {}): DevelopmentMembershipApi {
  const records = structuredClone(seedMemberships);
  const plans = structuredClone(seedPlans);
  const grantGroups = structuredClone(seedGrantGroups);
  const fulfilledOrders = new Set<string>();
  const withNames = (item: AdminMembership): AdminMembership => ({ ...item, ...namesFor(plans, item.appId, item.level) });
  const resolveApp = async (appId: string): Promise<MembershipAppLookup> => {
    if (options.getApp) {
      const app = await options.getApp(appId).catch(() => {
        throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
      });
      if (!app) throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
      return app;
    }
    const app = demoApps[appId];
    if (!app) throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
    return app;
  };
  const api: DevelopmentMembershipApi = {
    async list(query = {}): Promise<AdminMembershipListResult> {
      const parsed = adminMembershipListQuerySchema.parse(query);
      const scoped = records.map((item) => ({ ...item, membership: withNames(withEffective(item.membership)) })).filter((item) => matchesQuery(item.membership, parsed));
      const filtered = scoped.filter((item) => parsed.status === "all" || item.membership.status === parsed.status);
      const offset = (parsed.page - 1) * parsed.pageSize;
      const count = (status: StoredMembershipStatus) => scoped.filter((item) => item.membership.status === status).length;
      return {
        items: filtered.slice(offset, offset + parsed.pageSize).map((item) => item.membership),
        total: filtered.length,
        summary: { total: scoped.length, active: count("active"), expired: count("expired"), canceled: count("canceled") },
      };
    },
    async get(membershipId) {
      const value = records.find((item) => item.membership.id === membershipId);
      if (!value) throw new M1ApiError("MEMBERSHIP_NOT_FOUND", "会员记录不存在");
      return { membership: withNames(withEffective(value.membership)), events: structuredClone(value.events) };
    },
    async grant(input: AdminGrantMembershipInput) {
      const app = await resolveApp(input.appId);
      const user = demoUsers[input.userId];
      if (!user) throw new M1ApiError("USER_NOT_FOUND", "用户不存在");
      const existingByKey = records.find((item) => item.membership.appId === input.appId && item.membership.idempotencyKey === input.idempotencyKey);
      if (existingByKey) {
        if (existingByKey.membership.userId !== input.userId || existingByKey.membership.level !== input.level || existingByKey.membership.expiresAt !== input.expiresAt) {
          throw new M1ApiError("IDEMPOTENCY_CONFLICT", "幂等键已用于其他会员参数");
        }
        return { membership: withNames(withEffective(existingByKey.membership)), events: structuredClone(existingByKey.events) };
      }
      const at = now();
      const index = records.findIndex((item) => item.membership.appId === input.appId && item.membership.userId === input.userId);
      const next: AdminMembership = {
        id: index >= 0 ? records[index]!.membership.id : `memb_demo_${Date.now()}`,
        appId: input.appId, appName: app.name, appCode: app.code,
        userId: input.userId, userPublicId: user.publicId, userName: user.name,
        member: true, status: "active", level: input.level, ...namesFor(plans, input.appId, input.level),
        expiresAt: input.expiresAt, startedAt: index >= 0 && records[index]!.membership.status === "active" ? records[index]!.membership.startedAt : at,
        canceledAt: null, idempotencyKey: input.idempotencyKey,
        version: index >= 0 ? records[index]!.membership.version + 1 : 1,
        createdAt: index >= 0 ? records[index]!.membership.createdAt : at, updatedAt: at,
      };
      const previous = index >= 0 ? withEffective(records[index]!.membership).status : "none";
      const events = index >= 0 ? records[index]!.events : [];
      events.push(event(next.id, "status", "active", { fromStatus: previous, createdAt: at, note: index >= 0 ? "开通或续期" : "开通会员" }));
      events.push(event(next.id, "notification", "active", { fromStatus: "active", eventKey: "membership.granted", channel: "in_app", note: "站内信已记录，未发送短信", createdAt: at }));
      options.inbox?.enqueue({
        appId: next.appId, appName: next.appName, userId: next.userId,
        eventKey: "membership.granted", occurrenceKey: next.id, category: "transactional",
        title: "会员已开通", titleUg: "ئەزالىق ئېچىلدى", body: "当前应用会员已开通或续期。", bodyUg: "نۆۋەتتىكى ئەپ ئەزالىقى ئېچىلدى ياكى ئۇزارتىلدى.", createdAt: at,
      });
      options.webhooks?.enqueue({
        appId: next.appId,
        eventKey: "membership.granted",
        occurrenceKey: `${next.id}:membership.granted`,
        payload: {
          event: "membership.granted",
          appId: next.appId,
          occurredAt: at,
          data: { membershipId: next.id, userId: next.userId, level: next.level, status: next.status, expiresAt: next.expiresAt },
        },
      });
      const detail = { membership: next, events };
      if (index >= 0) records[index] = detail;
      else records.unshift(detail);
      return structuredClone(detail);
    },
    async cancel(membershipId, input: CancelMembershipInput) {
      const current = records.find((item) => item.membership.id === membershipId);
      if (!current) throw new M1ApiError("MEMBERSHIP_NOT_FOUND", "会员记录不存在");
      if (current.membership.version !== input.expectedVersion) throw new M1ApiError("MEMBERSHIP_STALE", "会员记录已被更新，请刷新后重试");
      const previous = withEffective(current.membership).status;
      if (previous === "canceled") throw new M1ApiError("MEMBERSHIP_ALREADY_CANCELED", "会员已经取消");
      const at = now();
      current.membership = { ...current.membership, status: "canceled", member: false, canceledAt: at, version: current.membership.version + 1, updatedAt: at };
      current.events.push(event(membershipId, "status", "canceled", { fromStatus: previous, createdAt: at, note: "取消会员" }));
      current.events.push(event(membershipId, "notification", "canceled", { fromStatus: "canceled", eventKey: "membership.canceled", channel: "in_app", note: "站内信已记录，未发送短信", createdAt: at }));
      options.inbox?.enqueue({
        appId: current.membership.appId, appName: current.membership.appName, userId: current.membership.userId,
        eventKey: "membership.canceled", occurrenceKey: membershipId, category: "transactional",
        title: "会员已取消", titleUg: "ئەزالىق بىكار قىلىندى", body: "当前应用会员已取消。", bodyUg: "نۆۋەتتىكى ئەپ ئەزالىقى بىكار قىلىندى.", createdAt: at,
      });
      options.webhooks?.enqueue({
        appId: current.membership.appId,
        eventKey: "membership.canceled",
        occurrenceKey: `${membershipId}:membership.canceled`,
        payload: {
          event: "membership.canceled",
          appId: current.membership.appId,
          occurredAt: at,
          data: {
            membershipId,
            userId: current.membership.userId,
            level: current.membership.level,
            status: current.membership.status,
            expiresAt: current.membership.expiresAt,
          },
        },
      });
      return structuredClone({ membership: withNames(current.membership), events: current.events });
    },
    async listPlans(query = {}) {
      const parsed = membershipPlanListQuerySchema.parse(query);
      const items = plans.filter((item) => !parsed.appId || item.appId === parsed.appId);
      return { items: structuredClone(items) };
    },
    async upsertPlan(input: MembershipPlanUpsertInput) {
      const parsed = membershipPlanUpsertSchema.parse(input);
      const app = await resolveApp(parsed.appId);
      const pricing = {
        amountMinor: parsed.amountMinor,
        currency: parsed.currency,
        periodDays: parsed.periodDays ?? null,
        period: parsed.period ?? null,
      };
      const index = plans.findIndex((item) => item.appId === parsed.appId && item.level === parsed.level);
      if (index >= 0) {
        const current = plans[index]!;
        if (parsed.expectedVersion && current.version !== parsed.expectedVersion) throw new M1ApiError("MEMBERSHIP_PLAN_STALE", "等级名称已被更新，请刷新后重试");
        plans[index] = { ...current, name: parsed.name, nameUg: parsed.nameUg, ...pricing, version: current.version + 1, updatedAt: now() };
        return structuredClone(plans[index]!);
      }
      const created: MembershipPlan = {
        appId: parsed.appId, appName: app.name, appCode: app.code, level: parsed.level,
        name: parsed.name, nameUg: parsed.nameUg, ...pricing, version: 1, updatedAt: now(),
      };
      plans.unshift(created);
      return structuredClone(created);
    },
    async deletePlan(appId, level) {
      const index = plans.findIndex((item) => item.appId === appId && item.level === level);
      if (index < 0) throw new M1ApiError("MEMBERSHIP_PLAN_NOT_FOUND", "会员等级不存在");
      plans.splice(index, 1);
      return { ok: true };
    },
    async listGrantGroups() {
      return { items: structuredClone(grantGroups) };
    },
    async upsertGrantGroup(input: MembershipGrantGroupUpsertInput) {
      const parsed = membershipGrantGroupUpsertSchema.parse(input);
      const owner = await resolveApp(parsed.ownerAppId);
      const members: MembershipGrantGroup["memberApps"] = [];
      for (const memberId of parsed.memberAppIds) {
        const member = await resolveApp(memberId);
        if (grantGroups.some((group) => group.ownerAppId !== parsed.ownerAppId && (group.ownerAppId === memberId || group.memberAppIds.includes(memberId)))) {
          throw new M1ApiError("GRANT_GROUP_CONFLICT", "该应用已加入其他授权组或已是主应用");
        }
        members.push({ id: memberId, name: member.name, code: member.code });
      }
      if (grantGroups.some((group) => group.memberAppIds.includes(parsed.ownerAppId))) {
        throw new M1ApiError("GRANT_GROUP_CONFLICT", "该应用已加入其他授权组或已是主应用");
      }
      const index = grantGroups.findIndex((item) => item.ownerAppId === parsed.ownerAppId);
      if (index >= 0) {
        const current = grantGroups[index]!;
        if (parsed.expectedVersion && current.version !== parsed.expectedVersion) throw new M1ApiError("MEMBERSHIP_STALE", "会员记录已被更新，请刷新后重试");
        grantGroups[index] = {
          ...current, ownerAppName: owner.name, ownerAppCode: owner.code,
          memberAppIds: [...parsed.memberAppIds], memberApps: members,
          version: current.version + 1, updatedAt: now(),
        };
        return structuredClone(grantGroups[index]!);
      }
      const created: MembershipGrantGroup = {
        ownerAppId: parsed.ownerAppId, ownerAppName: owner.name, ownerAppCode: owner.code,
        memberAppIds: [...parsed.memberAppIds], memberApps: members, version: 1, updatedAt: now(),
      };
      grantGroups.unshift(created);
      return structuredClone(created);
    },
    async deleteGrantGroup(ownerAppId) {
      const index = grantGroups.findIndex((item) => item.ownerAppId === ownerAppId);
      if (index < 0) throw new M1ApiError("MEMBERSHIP_NOT_FOUND", "会员记录不存在");
      grantGroups.splice(index, 1);
      return { ok: true };
    },
    async fulfillFromPaidOrder(order) {
      if (fulfilledOrders.has(order.id)) return;
      const levelRaw = order.payload.level ?? order.payload.planId;
      const level = typeof levelRaw === "string" ? levelRaw.trim() : "";
      if (!/^[A-Za-z0-9_-]{1,32}$/.test(level)) return;
      const plan = plans.find((item) => item.appId === order.appId && item.level === level);
      const daysRaw = order.payload.days ?? order.payload.periodDays ?? plan?.periodDays;
      const days = typeof daysRaw === "number" ? daysRaw : typeof daysRaw === "string" ? Number(daysRaw) : NaN;
      if (!Number.isInteger(days) || days < 1) return;
      let base = new Date(order.paidAt ?? Date.now());
      const existing = records.find((item) => item.membership.appId === order.appId && item.membership.userId === order.userId);
      if (existing?.membership.expiresAt && withEffective(existing.membership).status === "active") {
        const expires = new Date(existing.membership.expiresAt);
        if (expires.getTime() > base.getTime()) base = expires;
      }
      const expiresAt = new Date(base.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
      await api.grant({
        appId: order.appId,
        userId: order.userId,
        level,
        expiresAt,
        idempotencyKey: `order:${order.id}`,
      });
      fulfilledOrders.add(order.id);
    },
  };
  return api;
}
